import { useCallback, useMemo } from 'react';
import AddReviewBanner from '../AddReviewBanner';
import RenderLoadingReview from '@components/Reviews/RenderLoadingReview';
import {
  IHandleBIGenericClick,
  ReviewData,
  ReviewsInfo,
  Template,
} from '@common/types';
import {
  IDeleteReply,
  WriteReplyToReview,
  ReviewsContentProps,
  InfiniteScrollProps,
} from '@components/Reviews/types';
import { templateReviewsSectionTranslations } from '@utils/constants';
import DefaultReviewsHeader from '@components/Reviews/DefaultRenderedComponents/ReviewsHeader';
import SingleReviewHeader from '@components/Template/TemplateReviews/SingleReviewHeader';

type TemplateReviewsConfigProps = {
  template: Template;
  reviewsInfo: ReviewsInfo;
  loadNewReview?: boolean;
  userReviewForTemplate?: ReviewData | null;
  showReviewBanner?: boolean;
  openReviewModal: (openUsingBanner?: boolean) => void;
  handleBiGenericClick: IHandleBIGenericClick;
  deleteReply: IDeleteReply;
  writeReplyToReview: WriteReplyToReview;
  onEdit: () => void;

  // Component props
  isMobileView: boolean;
  showReviewsCount: boolean;
};

type UseTemplateReviewsReturn = {
  contentProps: ReviewsContentProps;
  infiniteScrollProps: InfiniteScrollProps;
};

export const useTemplateReviewsConfig = ({
  template,
  reviewsInfo,
  loadNewReview,
  userReviewForTemplate,
  showReviewBanner,
  openReviewModal,
  handleBiGenericClick,
  deleteReply,
  writeReplyToReview,
  onEdit,
  isMobileView,
  showReviewsCount,
}: TemplateReviewsConfigProps): UseTemplateReviewsReturn => {
  // ==================== COMPONENT DEFINITIONS ====================

  const TemplateLoadingReviews = useCallback(
    ({
      isInitialLoading,
      isMobileView,
    }: {
      isInitialLoading: boolean;
      isMobileView?: boolean;
    }) =>
      isInitialLoading ? (
        <RenderLoadingReview />
      ) : (
        <RenderLoadingReview hideDivider={true} />
      ),
    [],
  );

  // ==================== BUILD CONTENT CONFIGURATION ====================

  // Core data and shared functionality - changes with reviews/template
  const coreProps = useMemo(
    () => ({
      reviews: reviewsInfo.reviews,
      reviewsCount: reviewsInfo.total,
      averageReviewRating: reviewsInfo.average,
      i18nKeys: templateReviewsSectionTranslations,
      isMobileView,
      isMyProfile: template.isOwnerView,
      handleBiGenericClick,
      onEdit,
      userReview: userReviewForTemplate,
      replyProps: {
        hasActionMenu: true,
        isUsingReplyModal: true,
        writeReplyToReview,
        deleteReply,
        replyStyleProps: {
          writeReplyBoxVariant: 'full' as const,
        },
      },
    }),
    [
      reviewsInfo.reviews,
      reviewsInfo.total,
      reviewsInfo.average,
      isMobileView,
      template.isOwnerView,
      handleBiGenericClick,
      onEdit,
      userReviewForTemplate,
      writeReplyToReview,
      deleteReply,
    ],
  );

  // Header configuration - rarely changes
  const headerProps = useMemo(
    () => ({
      title: templateReviewsSectionTranslations.reviewsSectionTitle,
      ReviewsHeader: DefaultReviewsHeader,
      showReviewsCount,
    }),
    [showReviewsCount],
  );

  // Loading and banner functionality - changes with loading/banner state
  const functionalityProps = useMemo(
    () => ({
      loadNewReview,
      enableNoReviews: true,
      AddNewReviewBanner:
        !loadNewReview && showReviewBanner
          ? () => (
              <AddReviewBanner
                openReviewModal={openReviewModal}
                handleBiGenericClick={handleBiGenericClick}
              />
            )
          : undefined,
      LoadingReviews: TemplateLoadingReviews,
    }),
    [
      loadNewReview,
      showReviewBanner,
      openReviewModal,
      handleBiGenericClick,
      TemplateLoadingReviews,
    ],
  );

  // List configuration - changes when review components change
  const listProps = useMemo(
    () => ({
      reviewConfig: {
        shouldShowDivider: (index: number, total: number) => index + 1 < total,
        ReviewHeader: ({ review }: { review: ReviewData }) => (
          <SingleReviewHeader
            review={
              {
                ...review,
                overallScore: review.overallScore || review.score?.average || 0,
              } as ReviewData
            }
            isMobileView={isMobileView}
            showActionButtons={review.id === userReviewForTemplate?.id}
            onEdit={onEdit}
            handleBiGenericClick={handleBiGenericClick}
            i18nKeys={templateReviewsSectionTranslations}
          />
        ),
      },
    }),
    [
      isMobileView,
      template.isOwnerView,
      onEdit,
      handleBiGenericClick,
      userReviewForTemplate,
    ],
  );

  const contentProps = useMemo(
    () => ({
      ...coreProps,
      ...headerProps,
      ...functionalityProps,
      listProps,
    }),
    [coreProps, headerProps, functionalityProps, listProps],
  );

  const infiniteScrollProps = useMemo(
    () => ({
      cursor: '',
      fetchMoreOnScroll: false,
      fetchNextReviews: async () => {},
    }),
    [],
  );

  return {
    contentProps,
    infiniteScrollProps,
  };
};
